import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { disabledInputStyles, showToast, Text } from '@diamond/shared/ui';
import { formatDateRange } from '@diamond/shared/utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  useCheckVoucherCodeMutation,
  useUploadVoucherBannerMutation,
  useVoucherCode,
} from '../../hooks';
import type { CreateVoucherRequest } from '../../types';
import { ImageDropzone } from '../image-dropzone';

interface VoucherInformationFormProps {
  form: UseFormReturn<CreateVoucherRequest>;
  disabled?: boolean;
}

export function VoucherInformationForm({
  form,
  disabled = false,
}: VoucherInformationFormProps) {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    getValues,
  } = form;
  const toast = useToast();

  const periodStartDate = watch('period_start_date');
  const periodEndDate = watch('period_end_date');

  const checkCodeMutation = useCheckVoucherCodeMutation();
  const uploadBannerMutation = useUploadVoucherBannerMutation({
    onSuccess: (result) => {
      const previousUrl = getValues('banner_url');
      if (previousUrl && previousUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previousUrl);
      }
      setValue('banner_url', result.url, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      clearErrors('banner_url');
      showToast(toast, 'success', 'Banner berhasil diunggah');
    },
    onError: (error) => {
      showToast(
        toast,
        'error',
        error.message || 'Gagal mengunggah banner voucher'
      );
    },
  });

  const titleValue = watch('title') ?? '';
  const descriptionValue = watch('desc') ?? '';
  const handleBannerUpload = (files: File[]) => {
    if (!files?.length) return;
    uploadBannerMutation.mutate(files[0]);
  };

  const {
    codeRegister,
    codeStatus,
    codeMessage,
    isCheckingCode,
    canCheckCode,
    showClearButton,
    showValidIcon,
    inputStatusStyles,
    handleCheckCode,
    handleClearCode,
  } = useVoucherCode({
    form,
    mutation: {
      mutateAsync: checkCodeMutation.mutateAsync,
      isPending: checkCodeMutation.isPending,
    },
  });

  useEffect(() => {
    register('codeValidated');
  }, [register]);

  return (
    <VStack spacing={6} align="stretch">
      <Text fontSize="lg" fontWeight="semibold" color="gray.800">
        Info Dasar
      </Text>

      {/* File Upload Section */}
      <Box>
        <ImageDropzone
          name="banner_url"
          accept={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
          }}
          maxSize={2}
          errors={errors}
          placeholder="Drag and drop file here"
          onFileUpload={handleBannerUpload}
          isUploading={uploadBannerMutation.isPending}
          disabled={disabled}
        />
        <Text fontSize="xs" color="gray.500" mt={2}>
          Hanya menerima format PNG, JPG dengan ukuran maksimal 2 MB
          <br />
          Banner Resolution: 1300Pixels x 341Pixels
        </Text>
      </Box>

      {/* Form Fields */}
      <VStack spacing={5} align="stretch">
        {/* Judul Voucher */}
        <FormControl isInvalid={!!errors.title}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            Judul Voucher
          </FormLabel>
          <Input
            {...register('title')}
            placeholder="Masukkan Judul Banner"
            maxLength={100}
            fontSize="sm"
            autoComplete="off"
            isDisabled={disabled}
            sx={disabledInputStyles}
          />
          <Text fontSize="xs" color="gray.500" textAlign="right" mt={1}>
            {titleValue.length}/100
          </Text>
          <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
        </FormControl>

        {/* Kode Voucher */}
        <FormControl isInvalid={!!errors.code || !!errors.codeValidated}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            Kode Voucher
          </FormLabel>
          <Box display="flex" gap={3} alignItems="center">
            <InputGroup flex={1} size="md">
              <Input
                {...codeRegister}
                placeholder="Masukkan kode voucher"
                fontSize="sm"
                autoComplete="off"
                isDisabled={disabled || isCheckingCode}
                {...(disabled ? {} : inputStatusStyles)}
                sx={disabledInputStyles}
              />
              {!disabled && (
                <InputRightElement width="3rem">
                  {showClearButton ? (
                    <IconButton
                      aria-label="Hapus kode voucher"
                      icon={<CloseIcon fontSize="small" />}
                      variant="ghost"
                      size="sm"
                      color={codeStatus === 'invalid' ? 'red.500' : 'gray.500'}
                      onClick={handleClearCode}
                    />
                  ) : showValidIcon ? (
                    <CheckCircleIcon
                      fontSize="small"
                      style={{ color: '#16a34a' }}
                    />
                  ) : null}
                </InputRightElement>
              )}
            </InputGroup>
            {!disabled && (
              <Button
                type="button"
                onClick={handleCheckCode}
                isLoading={isCheckingCode}
                loadingText="Di cek"
                isDisabled={!canCheckCode}
                bg={canCheckCode ? '#1d4ed8' : '#f3f4f6'}
                color={canCheckCode ? '#ffffff' : '#6b7280'}
                _hover={
                  canCheckCode
                    ? {
                        bg: '#1e40af',
                      }
                    : undefined
                }
                _active={
                  canCheckCode
                    ? {
                        bg: '#1e3a8a',
                      }
                    : undefined
                }
                minW="120px"
              >
                Cek Kode
              </Button>
            )}
          </Box>
          {codeStatus === 'valid' && (
            <Text fontSize="sm" color="#34C759" mt={2}>
              {codeMessage || 'Kode voucher dapat digunakan'}
            </Text>
          )}
          <FormErrorMessage>
            {errors.code?.message ||
              errors.codeValidated?.message ||
              (codeStatus === 'invalid' ? codeMessage : '')}
          </FormErrorMessage>
        </FormControl>

        {/* Masa Berlaku */}
        <FormControl
          isInvalid={!!errors.period_start_date || !!errors.period_end_date}
        >
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            Masa Berlaku
          </FormLabel>
          <Box display="flex" gap={3} alignItems="center">
            <Input
              {...register('period_start_date')}
              type="date"
              placeholder="dd/mm/yyyy"
              fontSize="sm"
              isDisabled={disabled}
              sx={disabledInputStyles}
            />
            <Text color="gray.400" fontSize="sm">
              -
            </Text>
            <Input
              {...register('period_end_date')}
              type="date"
              placeholder="dd/mm/yyyy"
              fontSize="sm"
              isDisabled={disabled}
              sx={disabledInputStyles}
            />
          </Box>
          {periodStartDate && periodEndDate && (
            <Text fontSize="xs" color="gray.600" mt={2}>
              Preview: {formatDateRange(periodStartDate, periodEndDate)}
            </Text>
          )}
          <FormErrorMessage>
            {errors.period_start_date?.message ||
              errors.period_end_date?.message}
          </FormErrorMessage>
        </FormControl>

        {/* Deskripsi */}
        <FormControl isInvalid={!!errors.desc}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            Deskripsi
          </FormLabel>
          <Textarea
            {...register('desc')}
            placeholder="Masukkan deskripsi voucher"
            rows={4}
            maxLength={200}
            fontSize="sm"
            resize="vertical"
            isDisabled={disabled}
            sx={disabledInputStyles}
          />
          <Text fontSize="xs" color="gray.500" textAlign="right" mt={1}>
            {descriptionValue.length}/200
          </Text>
          <FormErrorMessage>{errors.desc?.message}</FormErrorMessage>
        </FormControl>
      </VStack>
    </VStack>
  );
}
